/**
 * Utility functions for GST calculations and validations
 */

/**
 * Check if a GSTN is valid
 * @param gstin GSTN to validate
 * @returns Boolean indicating if GSTN is valid
 */
export const isValidGSTN = (gstin: string): boolean => {
  if (!gstin) return false;

  // GSTN format: 22AAAAA0000A1Z5
  // 2 digits state code + 10 digit PAN + 1 digit entity number + 1 digit Z + 1 digit checksum
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

  return gstinRegex.test(gstin);
};

/**
 * Get state name from GSTN state code
 * @param gstin GSTN to extract state from
 * @returns State name
 */
export const getStateFromGSTN = (gstin: string): string => {
  if (!gstin || gstin.length < 2) return 'Unknown';

  const stateCode = gstin.substring(0, 2);
  const stateMap: { [key: string]: string } = {
    '01': 'Jammu and Kashmir',
    '02': 'Himachal Pradesh',
    '03': 'Punjab',
    '04': 'Chandigarh',
    '05': 'Uttarakhand',
    '06': 'Haryana',
    '07': 'Delhi',
    '08': 'Rajasthan',
    '09': 'Uttar Pradesh',
    '10': 'Bihar',
    '11': 'Sikkim',
    '12': 'Arunachal Pradesh',
    '13': 'Nagaland',
    '14': 'Manipur',
    '15': 'Mizoram',
    '16': 'Tripura',
    '17': 'Meghalaya',
    '18': 'Assam',
    '19': 'West Bengal',
    '20': 'Jharkhand',
    '21': 'Odisha',
    '22': 'Chhattisgarh',
    '23': 'Madhya Pradesh',
    '24': 'Gujarat',
    '26': 'Dadra and Nagar Haveli and Daman and Diu',
    '27': 'Maharashtra',
    '28': 'Andhra Pradesh',
    '29': 'Karnataka',
    '30': 'Goa',
    '31': 'Lakshadweep',
    '32': 'Kerala',
    '33': 'Tamil Nadu',
    '34': 'Puducherry',
    '35': 'Andaman and Nicobar Islands',
    '36': 'Telangana',
    '37': 'Andhra Pradesh (New)',
    '38': 'Ladakh',
    '97': 'Other Territory',
  };

  return stateMap[stateCode] || 'Unknown';
};

/**
 * Get GST state code from state name
 * @param stateName Name of the state
 * @returns GST state code (2 digits) or undefined if not found
 */
export const getStateCode = (stateName: string): string | undefined => {
  const stateMap: { [key: string]: string } = {
    'Jammu and Kashmir': '01',
    'Himachal Pradesh': '02',
    'Punjab': '03',
    'Chandigarh': '04',
    'Uttarakhand': '05',
    'Haryana': '06',
    'Delhi': '07',
    'Rajasthan': '08',
    'Uttar Pradesh': '09',
    'Bihar': '10',
    'Sikkim': '11',
    'Arunachal Pradesh': '12',
    'Nagaland': '13',
    'Manipur': '14',
    'Mizoram': '15',
    'Tripura': '16',
    'Meghalaya': '17',
    'Assam': '18',
    'West Bengal': '19',
    'Jharkhand': '20',
    'Odisha': '21',
    'Chhattisgarh': '22',
    'Madhya Pradesh': '23',
    'Gujarat': '24',
    'Dadra and Nagar Haveli and Daman and Diu': '26',
    'Maharashtra': '27',
    'Andhra Pradesh': '28',
    'Karnataka': '29',
    'Goa': '30',
    'Lakshadweep': '31',
    'Kerala': '32',
    'Tamil Nadu': '33',
    'Puducherry': '34',
    'Andaman and Nicobar Islands': '35',
    'Telangana': '36',
    'Andhra Pradesh (New)': '37',
    'Ladakh': '38',
  };

  return stateMap[stateName];
};

/**
 * Determine if it's an interstate supply based on GSTNs
 * @param sellerGSTN Seller's GSTN
 * @param buyerGSTN Buyer's GSTN
 * @returns Boolean indicating if it's an interstate supply
 */
export const isInterStateSupply = (sellerGSTN: string, buyerGSTN: string): boolean => {
  if (!sellerGSTN || !buyerGSTN) return false;

  // Extract first two digits from both GSTNs
  const sellerStateCode = sellerGSTN.substring(0, 2);
  const buyerStateCode = buyerGSTN.substring(0, 2);

  // If state codes are different, it's interstate supply
  return sellerStateCode !== buyerStateCode;
};

/**
 * Enhanced determination of interstate status
 * @param sellerGSTN Seller's GSTN (can be empty)
 * @param buyerGSTN Buyer's GSTN (can be empty)
 * @returns Boolean indicating if it's an interstate supply
 */
export const determineGSTType = (sellerGSTN: string, buyerGSTN: string): boolean => {
  // If either GSTN is empty or invalid format, default to intrastate (false)
  if (!sellerGSTN || !buyerGSTN) {
    console.log('One or both GSTNs are empty, defaulting to intrastate');
    return false;
  }

  // Basic validation check - make sure we have at least 2 characters
  if (sellerGSTN.length < 2 || buyerGSTN.length < 2) {
    console.log('GSTN length insufficient, defaulting to intrastate');
    return false;
  }

  // Extract first two digits from both GSTNs (state codes)
  const sellerStateCode = sellerGSTN.substring(0, 2);
  const buyerStateCode = buyerGSTN.substring(0, 2);

  // Check if they're valid state codes (numeric)
  if (isNaN(Number(sellerStateCode)) || isNaN(Number(buyerStateCode))) {
    console.log('Invalid state codes in GSTN, defaulting to intrastate');
    return false;
  }

  // Compare state codes - different means interstate
  const isInterState = sellerStateCode !== buyerStateCode;

  if (isInterState) {
    console.log('Interstate supply detected');
  } else {
    console.log('Intrastate supply detected');
  }

  return isInterState;
};

/**
 * Get common HSN codes with descriptions
 * @returns Array of HSN codes with descriptions
 */
export const getCommonHSNCodes = (): Array<{ code: string, description: string }> => {
  return [
    { code: '9983', description: 'Professional, Technical & Business Services' },
    { code: '9984', description: 'Telecommunications, Broadcasting and Information Supply Services' },
    { code: '9985', description: 'Support Services' },
    { code: '9986', description: 'Support Services to Agriculture, Hunting, Forestry, Fishing, Mining and Utilities' },
    { code: '9987', description: 'Maintenance, Repair and Installation Services' },
    { code: '9988', description: 'Manufacturing Services on Physical Inputs Owned by Others' },
    { code: '9989', description: 'Other Manufacturing Services' },
    { code: '998911', description: 'Publishing, Printing and Reproduction Services' },
    { code: '9982', description: 'Legal and Accounting Services' },
    { code: '998214', description: 'Accounting, Auditing and Bookkeeping Services' },
    { code: '998219', description: 'Other Accounting, Bookkeeping Services' },
    { code: '998221', description: 'Financial Auditing Services' },
    { code: '9954', description: 'Construction Services' },
    { code: '8471', description: 'Computers and Computer Peripherals' },
    { code: '8517', description: 'Telephones, Mobiles and Communication Equipment' },
  ];
};

/**
 * Calculate tax amounts for an invoice line item
 * @param taxableValue Taxable value of the item
 * @param gstRate GST rate applicable
 * @param isInterState Whether it's an interstate supply
 * @returns Object with calculated tax amounts
 */
export const calculateTaxAmounts = (
  taxableValue: number,
  gstRate: number,
  isInterState: boolean
): { cgst: number, sgst: number, igst: number } => {
  if (isInterState) {
    // IGST for interstate
    return {
      cgst: 0,
      sgst: 0,
      igst: (taxableValue * gstRate / 100)
    };
  } else {
    // CGST + SGST for intrastate
    return {
      cgst: (taxableValue * gstRate / 200), // Half of GST rate
      sgst: (taxableValue * gstRate / 200), // Half of GST rate
      igst: 0
    };
  }
};

/**
 * Get standard GST rates
 * @returns Array of standard GST rates
 */
export const getStandardGSTRates = (): number[] => {
  return [0, 3, 5, 12, 18, 28];
};

/**
 * Validate if invoice complies with GST requirements
 * @param invoice Invoice object to validate
 * @returns Object with validation status and errors
 */
export const validateGSTInvoice = (invoice: any): { isValid: boolean, errors: string[] } => {
  const errors: string[] = [];

  // Check required fields
  if (!invoice.invoiceNumber) errors.push('Invoice number is required');
  if (!invoice.date) errors.push('Invoice date is required');
  if (!invoice.client) errors.push('Client name is required');
  if (!invoice.clientDetails?.gstin) errors.push('Client GSTN is required');
  if (!invoice.clientDetails?.address) errors.push('Client address is required');

  // Validate items
  if (!invoice.items || invoice.items.length === 0) {
    errors.push('At least one item is required');
  } else {
    invoice.items.forEach((item: any, index: number) => {
      if (!item.name) errors.push(`Item #${index + 1}: Name is required`);
      if (!item.quantity || item.quantity <= 0) errors.push(`Item #${index + 1}: Valid quantity is required`);
      if (!item.rate || item.rate <= 0) errors.push(`Item #${index + 1}: Valid rate is required`);
      if (!item.hsnCode) errors.push(`Item #${index + 1}: HSN/SAC code is required`);
      if (item.gstRate === undefined) errors.push(`Item #${index + 1}: GST rate is required`);
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}; 